/*
 * The Imperial Circle (sections/imperial-circle.liquid).
 * Cloaks stand on a circle; drag, swipe, wheel, arrows or keyboard turn it and it always settles with one
 * cloak in front. The front cloak can be turned around on demand. Tapping it opens the shared quick view
 * panel (@theme/quick-view-panel), which adds to cart without leaving the page. Every cloak is also a plain
 * product link.
 */
import { prefersReducedMotion } from '@theme/utilities';
import { QuickViewPanel } from '@theme/quick-view-panel';

const STEP = Math.PI / 6; // 30 degrees between neighbours
const DRAG_THRESHOLD = 6;
const LANDED = 0.08; // fraction of a step from the front at which the cloak reads as stopped

/** @param {number} value @param {number} size */
const wrap = (value, size) => ((value % size) + size) % size;

class ImperialCircle extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLElement[]} */
  #items = [];
  #count = 0;
  #pos = 0;
  #target = 0;
  #active = -1;
  #frame = 0;
  #lastTime = 0;
  #radius = 300;
  #spacing = 150;
  #itemHeight = 400;
  #rtl = false;

  /** @type {{ id: number, x: number, y: number, pos: number, moved: boolean, samples: Array<[number, number]> } | null} */
  #drag = null;
  #suppressClick = false;
  #wheelTimer = 0;
  #userMoved = false;

  #manualTurn = false;
  #ringIndex = 0;

  #autoplayTimer = 0;
  #autoplayEnabled = false;
  #paused = false;
  #hovering = false;
  #inView = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {QuickViewPanel | null} */
  #panel = null;
  /** @type {Element | null} */
  #guide = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#items = Array.from(this.querySelectorAll('[data-circle-item]'));
    this.#count = this.#items.length;
    this.#panel = new QuickViewPanel(this, {
      source: 'imperial-circle',
      onToggle: () => this.#syncAutoplay(),
      fallbackFocus: '[data-circle-open]',
    });
    this.#guide = this.querySelector('[data-size-guide-home] .size-guide');
    this.#rtl = getComputedStyle(this).direction === 'rtl';
    if (!this.#count) return;

    const stage = this.querySelector('[data-circle-stage]');
    if (stage instanceof HTMLElement) this.#bindStage(stage, signal);

    this.querySelector('[data-circle-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-circle-next]')?.addEventListener('click', () => this.#step(1), { signal });
    this.querySelector('[data-circle-turn]')?.addEventListener('click', () => this.#toggleTurn(), { signal });
    for (const button of this.querySelectorAll('[data-circle-open]')) {
      button.addEventListener('click', () => this.#openPanel(this.#active, /** @type {HTMLElement} */ (button)), {
        signal,
      });
    }

    const pause = this.querySelector('[data-circle-pause]');
    pause?.addEventListener('click', () => this.#setPaused(!this.#paused), { signal });

    window.addEventListener('resize', () => this.#measure(), { passive: true, signal });
    document.addEventListener('visibilitychange', () => this.#syncAutoplay(), { signal });

    this.#panel.bind(signal);

    this.#autoplayEnabled = this.dataset.autoplay === 'true' && !prefersReducedMotion();
    if (!this.#autoplayEnabled && pause instanceof HTMLElement) pause.hidden = true;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#inView = Boolean(entry?.isIntersecting);
        this.#syncAutoplay();
        if (this.#inView && !this.#frame) this.#turnFront();
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);

    // On a product page the cloak being viewed starts in front.
    const start = wrap(Number(this.dataset.start) || 0, this.#count);
    this.#pos = start;
    this.#target = start;
    this.#measure();
    this.#setActive(start, false);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#panel?.destroy();
    this.#panel = null;
    this.#observer?.disconnect();
    this.#observer = null;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    clearInterval(this.#autoplayTimer);
    clearTimeout(this.#wheelTimer);
  }

  /* Layout */

  #measure() {
    const stage = this.querySelector('[data-circle-stage]');
    const first = this.#items[0];
    if (!(stage instanceof HTMLElement) || !first) return;

    const width = first.offsetWidth || 260;
    const wide = stage.clientWidth >= 750;
    this.#itemHeight = width / 0.7;
    this.#radius = Math.min(width * (wide ? 1.7 : 1.5), stage.clientWidth * 0.46);
    this.#spacing = this.#radius * Math.sin(STEP);
    this.#render();
  }

  #render() {
    const half = this.#count / 2;

    this.#items.forEach((item, index) => {
      let offset = wrap(index - this.#pos, this.#count);
      if (offset > half) offset -= this.#count;

      const angle = offset * STEP;
      const distance = Math.abs(offset);
      const depth = Math.max(Math.cos(angle), 0);
      const visible = distance < 3.2;
      const x = Math.sin(angle) * this.#radius * (this.#rtl ? -1 : 1);
      const y = -(1 - depth) * this.#itemHeight * 0.1;
      const scale = 0.5 + 0.5 * depth;
      const fade = Math.min(Math.max(3.2 - distance, 0), 1);

      item.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
      item.style.opacity = visible ? fade.toFixed(3) : '0';
      item.style.filter = `brightness(${(0.42 + 0.7 * depth).toFixed(3)}) saturate(${(0.75 + 0.4 * depth).toFixed(3)})`;
      item.style.zIndex = String(Math.round(depth * 50));
      item.style.visibility = visible ? 'visible' : 'hidden';
    });

    const progress = this.#count > 1 ? wrap(Math.round(this.#pos), this.#count) / (this.#count - 1) : 1;
    this.style.setProperty('--circle-progress', progress.toFixed(3));
  }

  /* Motion */

  #animate() {
    if (this.#frame) return;
    this.#lastTime = performance.now();
    this.classList.add('is-moving');

    const tick = (/** @type {number} */ now) => {
      const delta = Math.min(now - this.#lastTime, 64);
      this.#lastTime = now;

      if (!this.#drag?.moved) {
        const ease = prefersReducedMotion() ? 1 : 1 - Math.pow(0.84, delta / 16.7);
        this.#pos += (this.#target - this.#pos) * ease;
        if (Math.abs(this.#target - this.#pos) < 0.002) this.#pos = this.#target;
      }

      this.#render();
      this.#setActive(wrap(Math.round(this.#pos), this.#count), false);

      // The easing tail is invisible, so turn the cloak as soon as it has visibly landed.
      if (!this.#drag && Math.abs(this.#target - this.#pos) < LANDED) this.#turnFront();

      if (this.#pos === this.#target && !this.#drag) {
        this.#frame = 0;
        this.classList.remove('is-moving');
        this.#settled();
        return;
      }
      this.#frame = requestAnimationFrame(tick);
    };

    this.#frame = requestAnimationFrame(tick);
  }

  /** @param {number} direction */
  #step(direction, fromUser = true) {
    if (fromUser) this.#userInteracted();
    this.#target = Math.round(this.#target) + direction;
    this.#animate();
  }

  /** @param {number} index */
  #goTo(index) {
    const current = Math.round(this.#target);
    let delta = wrap(index - current, this.#count);
    if (delta > this.#count / 2) delta -= this.#count;
    if (!delta) return;
    this.#target = current + delta;
    this.#animate();
  }

  #settled() {
    // Keep positions small so they never drift into float trouble.
    if (Math.abs(this.#pos) > this.#count * 4) {
      this.#pos = wrap(this.#pos, this.#count);
      this.#target = this.#pos;
    }
    this.#preloadPanel(this.#active);
    this.#turnFront();
    this.#flashRing();

    if (this.#userMoved) {
      this.#userMoved = false;
      const item = this.#items[this.#active];
      const live = this.querySelector('[data-circle-live]');
      if (item && live) {
        const position = (this.dataset.positionText || '')
          .replace('%current%', String(this.#active + 1))
          .replace('%total%', String(this.#count));
        live.textContent = `${item.dataset.name}, ${item.dataset.price}. ${position}`;
      }
    }
  }

  /**
   * @param {number} index
   * @param {boolean} [force]
   */
  #setActive(index, force = true) {
    if (index === this.#active && !force) return;
    const previous = this.#items[this.#active];
    if (previous && index !== this.#active) {
      this.#setTurned(previous, false);
      this.#manualTurn = false;
    }

    this.#active = index;
    const item = this.#items[index];
    if (!item) return;

    this.#items.forEach((other) => other.classList.toggle('is-active', other === item));

    const set = (/** @type {string} */ selector, /** @type {string | undefined} */ value) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = value || '';
    };
    set('[data-circle-name]', item.dataset.name);
    set('[data-circle-line]', item.dataset.line);
    set('[data-circle-price]', item.dataset.price);
    set('[data-circle-compare]', item.dataset.compare);
    set('[data-circle-current]', String(index + 1).padStart(2, '0'));
  }

  /* Turn the front cloak around */

  #toggleTurn() {
    const item = this.#items[this.#active];
    if (!item) return;
    this.#userInteracted();
    // Once the visitor turns this cloak themselves, leave it the way they chose.
    this.#manualTurn = true;
    this.#setTurned(item, !item.classList.contains('is-flipped'));
  }

  /** Pulses the light ring on the floor when a new cloak steps into the front. */
  #flashRing() {
    if (this.#active === this.#ringIndex) return;
    this.#ringIndex = this.#active;
    this.classList.remove('is-arriving');
    void this.offsetWidth;
    this.classList.add('is-arriving');
  }

  /** Turns the front cloak around to show its back once it has landed in front. */
  #turnFront() {
    if (this.#manualTurn || !this.#inView || prefersReducedMotion()) return;
    const item = this.#items[this.#active];
    if (item && !item.classList.contains('is-flipped')) this.#setTurned(item, true);
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} turned
   */
  #setTurned(item, turned) {
    item.classList.toggle('is-flipped', turned);
    if (item !== this.#items[this.#active]) return;

    const button = this.querySelector('[data-circle-turn]');
    const label = this.querySelector('[data-circle-turn-label]');
    button?.setAttribute('aria-pressed', String(turned));
    if (label) label.textContent = (turned ? this.dataset.frontText : this.dataset.turnText) || '';
  }

  /* Input */

  /**
   * @param {HTMLElement} stage
   * @param {AbortSignal} signal
   */
  #bindStage(stage, signal) {
    stage.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0 || this.#drag) return;
        if (event.target instanceof Element && event.target.closest('[data-circle-open]')) return;
        this.#drag = { id: event.pointerId, x: event.clientX, y: event.clientY, pos: this.#pos, moved: false, samples: [] };
      },
      { signal }
    );

    stage.addEventListener(
      'pointermove',
      (event) => {
        const drag = this.#drag;
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x;

        if (!drag.moved) {
          if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) < Math.abs(event.clientY - drag.y)) return;
          drag.moved = true;
          stage.setPointerCapture(event.pointerId);
          stage.classList.add('is-dragging');
          this.#userInteracted();
          this.#animate();
        }

        this.#pos = drag.pos - (dx / this.#spacing) * (this.#rtl ? -1 : 1);
        this.#target = this.#pos;
        drag.samples.push([performance.now(), this.#pos]);
        if (drag.samples.length > 6) drag.samples.shift();
      },
      { signal }
    );

    const end = (/** @type {PointerEvent} */ event) => {
      const drag = this.#drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.#drag = null;
      if (!drag.moved) return;

      stage.classList.remove('is-dragging');
      this.#suppressClick = true;
      setTimeout(() => (this.#suppressClick = false), 0);

      const [first, last] = [drag.samples[0], drag.samples[drag.samples.length - 1]];
      const velocity = first && last && last[0] > first[0] ? (last[1] - first[1]) / (last[0] - first[0]) : 0;
      this.#target = Math.round(this.#pos + Math.max(Math.min(velocity * 120, 2), -2));
      this.#animate();
    };
    stage.addEventListener('pointerup', end, { signal });
    stage.addEventListener('pointercancel', end, { signal });

    stage.addEventListener(
      'click',
      (event) => {
        const link = event.target instanceof Element ? event.target.closest('[data-circle-link]') : null;
        if (!(link instanceof HTMLAnchorElement)) return;
        if (this.#suppressClick) {
          event.preventDefault();
          return;
        }
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();
        const item = link.closest('[data-circle-item]');
        const index = item ? this.#items.indexOf(/** @type {HTMLElement} */ (item)) : -1;
        if (index < 0) return;
        this.#userInteracted();
        if (index === this.#active && !this.#frame) {
          this.#openPanel(index, link);
        } else {
          this.#goTo(index);
        }
      },
      { signal }
    );

    stage.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        this.#userInteracted();
        this.#pos += (event.deltaX / this.#spacing) * (this.#rtl ? -1 : 1);
        this.#target = this.#pos;
        this.#animate();
        clearTimeout(this.#wheelTimer);
        this.#wheelTimer = window.setTimeout(() => {
          this.#target = Math.round(this.#pos);
          this.#animate();
        }, 120);
      },
      { passive: false, signal }
    );

    stage.addEventListener(
      'focusin',
      (event) => {
        const item = event.target instanceof Element ? event.target.closest('[data-circle-item]') : null;
        const index = item ? this.#items.indexOf(/** @type {HTMLElement} */ (item)) : -1;
        if (index < 0 || index === this.#active) return;
        this.#userInteracted();
        this.#goTo(index);
      },
      { signal }
    );

    stage.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (!(event.target instanceof Element) || !event.target.closest('[data-circle-link]')) return;
        event.preventDefault();
        const forward = (event.key === 'ArrowRight') !== this.#rtl;
        const next = this.#items[wrap(this.#active + (forward ? 1 : -1), this.#count)];
        next?.querySelector('a')?.focus({ preventScroll: true });
      },
      { signal }
    );

    stage.addEventListener('pointerenter', () => ((this.#hovering = true), this.#syncAutoplay()), { signal });
    stage.addEventListener('pointerleave', () => ((this.#hovering = false), this.#syncAutoplay()), { signal });
  }

  /* Autoplay */

  #userInteracted() {
    this.#userMoved = true;
    if (this.#autoplayEnabled && !this.#paused) this.#setPaused(true);
  }

  /** @param {boolean} paused */
  #setPaused(paused) {
    this.#paused = paused;
    const button = this.querySelector('[data-circle-pause]');
    if (button instanceof HTMLElement) {
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', (paused ? button.dataset.playLabel : button.dataset.pauseLabel) || '');
    }
    this.#syncAutoplay();
  }

  #syncAutoplay() {
    const run =
      this.#autoplayEnabled && !this.#paused && this.#inView && !this.#hovering && !document.hidden && !this.#panel?.isOpen;

    if (!run) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = 0;
      return;
    }
    if (this.#autoplayTimer) return;

    const seconds = Number(this.dataset.autoplaySpeed) || 6;
    this.#autoplayTimer = window.setInterval(() => {
      if (!this.#drag) this.#step(1, false);
    }, seconds * 1000);
  }

  /* Quick view panel */

  /** @param {number} index */
  #template(index) {
    const id = this.#items[index]?.dataset.panel;
    const template = id ? document.getElementById(id) : null;
    return template instanceof HTMLTemplateElement ? template : null;
  }

  /** @param {number} index */
  #preloadPanel(index) {
    QuickViewPanel.preload(this.#template(index));
  }

  /**
   * @param {number} index
   * @param {HTMLElement} [opener]
   */
  #openPanel(index, opener) {
    const template = this.#template(index);
    if (!template) return;
    this.#panel?.open(template, { url: this.#items[index]?.dataset.url, opener, guide: this.#guide });
  }
}

if (!customElements.get('imperial-circle')) customElements.define('imperial-circle', ImperialCircle);
